import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Container,
    Loader,
    Modal,
    Header,
} from 'semantic-ui-react';
import AppHeader from './AppHeader';
import DiditList from './DiditList';
import OwnedDiditList from './OwnedDiditList';
import LandingPage from './components/LandingPage';
import { TaskProvider, useTaskContext } from './context/TaskContext';
import * as api from './services/api';

function AppContentInner() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({});
    const [isCompactMode, setIsCompactMode] = useState(true);
    const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
    const [oauthResponse, setOauthResponse] = useState(null);

    const { loadTask } = useTaskContext();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            setOauthResponse(code);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                setLoading(true);
                setError(null);
                const userData = await api.checkAuth();
                setUser(userData);
            } catch (err) {
                console.error('Error checking authentication:', err);
                setError(err.message);
            } finally {
                setLoading(false);
                setAuthChecked(true);
            }
        };

        if (oauthResponse || !authChecked) {
            checkAuth();
        } else {
            setLoading(false);
        }
    }, [oauthResponse, authChecked]);

    // When location changes, load the task from context
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const parentId = params.get('p');
        if (parentId) {
            loadTask(parentId);
        }
    }, [location, loadTask]);

    const handleLogin = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const parentUUID = queryParams.get('p') || queryParams.get('parent');
        window.location.href = api.getLoginUrl(parentUUID);
    };

    const handleLogout = async () => {
        try {
            await api.logout();
            setUser(null);
            setAuthChecked(prev => !prev);
            setModalContent({
                header: "Logged Out",
                message: "You have been successfully logged out of this application. You are still connected to Google."
            });
            setModalOpen(true);
        } catch (err) {
            console.error('Error during logout:', err);
            setError(err.message);
        }
    };

    const handleDisconnect = async () => {
        try {
            await api.disconnect();
            setUser(null);
            setAuthChecked(prev => !prev);
            setModalContent({
                header: "Disconnected from Google",
                message: "Your account has been successfully disconnected from Google. You will need to re-authorize this application to use it again."
            });
            setModalOpen(true);
        } catch (err) {
            console.error('Error during disconnect:', err);
            setError(err.message);
        }
    };

    const InfoModal = () => (
        <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            size="small"
        >
            <Header icon="info circle" content={modalContent.header} />
            <Modal.Content>
                <p>{modalContent.message}</p>
            </Modal.Content>
            <Modal.Actions>
                <Button color='green' onClick={() => setModalOpen(false)}>
                    Understood
                </Button>
            </Modal.Actions>
        </Modal>
    );

    const getUserDisplayName = (u) => {
        return u.Name || u.Email || 'User';
    };

    const addTasks = async (tasks, listName) => {
        const queryParams = new URLSearchParams(window.location.search);
        let parentUUID = queryParams.get('p') || queryParams.get('parent');

        if (!parentUUID) {
            try {
                const res = await api.createParentTask({ label: listName || "New Task List" });
                parentUUID = res.uuid;
            } catch (err) {
                console.error("Error creating parent task:", err);
                return;
            }
        }

        for (const task of tasks) {
            try {
                await api.createChildTask(parentUUID, task.label, task.content);
            } catch (err) {
                console.error(`Error adding task ${task.label}:`, err);
            }
        }

        window.history.pushState({}, '', `?p=${parentUUID}`);
        loadTask(parentUUID);
    };

    const toggleViewMode = () => {
        setIsCompactMode(prevMode => !prevMode);
    };

    const handleTaskClick = (task) => {
        navigate(`/?p=${task.uuid}`);
        loadTask(task.uuid);
    };

    if (loading) {
        return (
            <Container text style={{ marginTop: '7em' }}>
                <Loader active>Loading</Loader>
            </Container>
        );
    }

    return (
        <div>
            <AppHeader
                user={user}
                addTasks={addTasks}
                getUserDisplayName={getUserDisplayName}
                handleLogout={handleLogout}
                handleLogin={handleLogin}
                isCompactMode={isCompactMode}
                toggleViewMode={toggleViewMode}
                isCreateTaskModalOpen={isCreateTaskModalOpen}
                setIsCreateTaskModalOpen={setIsCreateTaskModalOpen}
            />
            <Routes>
                <Route path="/" element={
                    !new URLSearchParams(location.search).has('p') && !new URLSearchParams(location.search).has('parent')
                        ? <LandingPage user={user} onLogin={handleLogin} onCreateTask={() => setIsCreateTaskModalOpen(true)} />
                        : <Container text style={{ marginTop: '4em' }}>
                            {error && <p style={{ color: 'red', padding: '0 1em' }}>{error}</p>}
                            <DiditList user={user} isCompactMode={isCompactMode} />
                          </Container>
                } />
                <Route path="/owned" element={
                    <Container text style={{ marginTop: '4em' }}>
                        {error && <p style={{ color: 'red', padding: '0 1em' }}>{error}</p>}
                        <OwnedDiditList
                            isCompactMode={isCompactMode}
                            onTaskClick={handleTaskClick}
                        />
                    </Container>
                } />
            </Routes>
            <InfoModal />
        </div>
    );
}

function AppContent() {
    return (
        <TaskProvider>
            <AppContentInner />
        </TaskProvider>
    );
}

export default AppContent;
